<template>
  <div id="app">
    <AppShell v-if="showShell">
      <router-view />
    </AppShell>
    <router-view v-else />
    <div v-if="showFloatingLanguage" class="floating-language-selector">
      <LanguageSelector />
    </div>
    <GlobalDialogHost />
  </div>
</template>

<script>
import AppShell from '@/app/AppShell.vue';
import GlobalDialogHost from '@/shared/ui/GlobalDialogHost.vue';
import LanguageSelector from '@/features/localization/components/LanguageSelector.vue';
import authService from '@/services/auth.js';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';

export default {
  name: 'App',
  components: {
    AppShell,
    GlobalDialogHost,
    LanguageSelector,
  },
  data() {
    return {
      // `systemFlags` is a shared read-only source for feature-flag gating.
      // Populated once at App boot by GET /api/system/flags. Child
      // components access it via `this.$root.systemFlags` (see e.g.
      // FolderNode.vue `advancedIdeasEnabled` computed). Empty object =
      // "still loading" — components should treat unknown flags as
      // permissive until the real values arrive.
      systemFlags: {},
    };
  },
  computed: {
    showFloatingLanguage() {
      const path = this.$route?.path || '/';
      return !this.showShell && path !== '/' && !path.startsWith('/referral') && !path.startsWith('/hq');
    },
    showShell() {
      // Hide shell on landing / auth pages — show it only for the authenticated app
      const path = this.$route?.path || '/';
      const bareRoutes = ['/', '/login', '/auth', '/register'];
      if (bareRoutes.includes(path) || path.startsWith('/referral') || path.startsWith('/hq')) return false;
      return authService.isAuthenticated();
    },
  },
  created() {
    this.updateDocumentTitle();
    authService.captureAttribution(this.$route?.query || {});
    this.fetchSystemFlags();
  },
  watch: {
    '$i18n.locale': 'updateDocumentTitle',
    '$route.query': {
      deep: true,
      handler(query) { authService.captureAttribution(query || {}); },
    },
  },
  methods: {
    updateDocumentTitle() {
      document.title = this.$i18n.locale === 'en'
        ? 'PaperNova — AI-assisted research workspace'
        : 'PaperNova — 科研 AI 辅助工作台';
    },
    async fetchSystemFlags() {
      // Best-effort: the endpoint is public (no auth needed) and returns a
      // tiny JSON blob. If it fails (offline, backend down) we silently
      // fall back to "all flags enabled", which is what FolderNode etc.
      // already do when the flag is absent. Never block app boot on this.
      try {
        const resp = await axios.get(`${API_BASE_URL}/api/system/flags`, {
          timeout: 5000,
        });
        if (resp && resp.data && typeof resp.data === 'object') {
          this.systemFlags = resp.data;
        }
      } catch (_err) {
        // keep empty → treated as permissive by consumers
      }
    },
  },
};
</script>


<style>
/* =====================================================================
   PaperNova — Academic Design System
   Professional research-oriented UI tuned for 1M+ university users.
   Palette: deep navy + academic blue. Headings: Chinese-first serif stack.
   ===================================================================== */

/* Design tokens are centralized in styles/design-tokens.css. */

/* ---------- Reset & Base ---------- */
*,
*::before,
*::after { box-sizing: border-box; }

html { -webkit-text-size-adjust: 100%; }

body {
  margin: 0;
  font-family: var(--font-family-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-normal);
  color: var(--dark);
  background-color: var(--light);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

#app { min-height: 100vh; position: relative; }
.floating-language-selector { position: fixed; top: 92px; right: 24px; z-index: var(--z-dropdown); padding: 4px; border-radius: var(--radius-md); background: rgba(255,255,255,.92); box-shadow: var(--shadow-sm); backdrop-filter: blur(10px); }
@media (max-width: 640px) { .floating-language-selector { top: 84px; right: 14px; } }

/* ---------- Typography — Serif headings for academic feel ---------- */
h1, h2, h3, h4 {
  margin: 0 0 var(--space-4) 0;
  font-family: var(--font-family-serif);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-tight);
  color: var(--primary);
  letter-spacing: -0.01em;
}
h5, h6 {
  margin: 0 0 var(--space-3) 0;
  font-family: var(--font-family-sans);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
  color: var(--primary);
}
h1 { font-size: var(--font-size-3xl); }
h2 { font-size: var(--font-size-2xl); }
h3 { font-size: var(--font-size-xl); }
h4 { font-size: var(--font-size-lg); }
h5 { font-size: var(--font-size-base); }
h6 { font-size: var(--font-size-sm); text-transform: uppercase; letter-spacing: 0.06em; }

p { margin: 0 0 var(--space-4) 0; }

code, kbd, pre, samp { font-family: var(--font-family-mono); font-size: 0.95em; }

/* ---------- Links ---------- */
a {
  color: var(--secondary);
  text-decoration: none;
  transition: color var(--transition-fast);
}
a:hover { color: var(--secondary-dark); text-decoration: underline; }

/* ---------- Buttons (flat, no gradients) ---------- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: 0.5rem 1rem;
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  line-height: 1.5;
  color: var(--dark-gray);
  background-color: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
  transition: background-color var(--transition-fast), border-color var(--transition-fast),
              color var(--transition-fast), box-shadow var(--transition-fast);
  text-decoration: none;
  user-select: none;
  white-space: nowrap;
}
.btn:hover:not(:disabled) {
  background-color: var(--light-gray);
  border-color: var(--border-strong);
  color: var(--dark);
}
.btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
  border-color: var(--secondary);
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-primary {
  background-color: var(--primary);
  border-color: var(--primary);
  color: var(--white);
}
.btn-primary:hover:not(:disabled) {
  background-color: var(--primary-dark);
  border-color: var(--primary-dark);
  color: var(--white);
}

.btn-secondary {
  background-color: var(--secondary);
  border-color: var(--secondary);
  color: var(--white);
}
.btn-secondary:hover:not(:disabled) {
  background-color: var(--secondary-dark);
  border-color: var(--secondary-dark);
  color: var(--white);
}

.btn-success {
  background-color: var(--success);
  border-color: var(--success);
  color: var(--white);
}
.btn-success:hover:not(:disabled) {
  background-color: var(--success-dark);
  border-color: var(--success-dark);
  color: var(--white);
}

.btn-warning {
  background-color: var(--warning);
  border-color: var(--warning);
  color: var(--white);
}
.btn-warning:hover:not(:disabled) {
  background-color: var(--warning-dark);
  border-color: var(--warning-dark);
  color: var(--white);
}

.btn-danger {
  background-color: var(--accent);
  border-color: var(--accent);
  color: var(--white);
}
.btn-danger:hover:not(:disabled) {
  background-color: var(--accent-dark);
  border-color: var(--accent-dark);
  color: var(--white);
}

.btn-info {
  background-color: var(--info);
  border-color: var(--info);
  color: var(--white);
}
.btn-info:hover:not(:disabled) {
  background-color: var(--info-dark);
  border-color: var(--info-dark);
  color: var(--white);
}

.btn-ghost {
  background-color: transparent;
  border-color: transparent;
  color: var(--dark-gray);
}
.btn-ghost:hover:not(:disabled) {
  background-color: var(--light-gray);
  border-color: transparent;
  color: var(--dark);
}

.btn-outline {
  background-color: transparent;
  border-color: var(--border-strong);
  color: var(--dark-gray);
}
.btn-outline:hover:not(:disabled) {
  background-color: var(--light);
  border-color: var(--secondary);
  color: var(--secondary-dark);
}

.btn-sm {
  padding: 0.25rem 0.625rem;
  font-size: var(--font-size-xs);
  gap: var(--space-1);
}
.btn-lg {
  padding: 0.75rem 1.5rem;
  font-size: var(--font-size-base);
}
.btn-icon-only {
  padding: 0.375rem;
  width: 2rem;
  height: 2rem;
}
.btn-icon-only.btn-sm { width: 1.75rem; height: 1.75rem; padding: 0.25rem; }
.btn-icon-only.btn-lg { width: 2.5rem; height: 2.5rem; padding: 0.5rem; }

/* ---------- Form Elements ---------- */
.form-control {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-normal);
  line-height: 1.5;
  color: var(--dark);
  background-color: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}
.form-control::placeholder { color: var(--medium-gray); }
.form-control:hover { border-color: var(--border-strong); }
.form-control:focus {
  border-color: var(--secondary);
  outline: 0;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}
.form-control:disabled {
  background-color: var(--light-gray);
  color: var(--medium-gray);
  cursor: not-allowed;
}
textarea.form-control { resize: vertical; min-height: 5rem; }

/* Consistent native-select treatment across feature pages. */
select:not([multiple]) {
  appearance: none !important;
  -webkit-appearance: none !important;
  padding-right: 2.5rem !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%235b7185' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m7 10 5 5 5-5'/%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.875rem center !important;
  background-size: 1rem !important;
  cursor: pointer;
}
select:not([multiple]):hover:not(:disabled) { border-color: #9aafc1; }
select:not([multiple]):focus-visible {
  border-color: var(--secondary);
  outline: 0;
  box-shadow: 0 0 0 3px rgba(37, 92, 244, 0.14);
}
select:not([multiple]):disabled {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m7 10 5 5 5-5'/%3E%3C/svg%3E") !important;
  cursor: not-allowed;
}
select::-ms-expand { display: none; }

/* Native customizable selects preserve Vue bindings, form validation and
   keyboard navigation. Their picker uses the top layer, including in modals.
   Browsers without base-select retain the native picker above. */
@supports (appearance: base-select) {
  select:not([multiple]):not([size]),
  select:not([multiple]):not([size])::picker(select) {
    appearance: base-select !important;
    -webkit-appearance: base-select !important;
  }

  select:not([multiple]):not([size]) {
    display: flex;
    align-items: center;
    gap: 12px;
    min-inline-size: 6rem;
    min-block-size: 38px;
    padding-inline-end: 12px !important;
    background-image: none !important;
    border-radius: var(--radius-md);
    transition: border-color .15s ease, box-shadow .15s ease;
  }

  select:not([multiple]):not([size])::picker-icon {
    content: '';
    flex: 0 0 7px;
    width: 7px;
    height: 7px;
    margin-inline-start: auto;
    margin-inline-end: 3px;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    color: var(--text-muted);
    transform: translateY(-2px) rotate(45deg);
    transition: transform .15s ease;
  }

  select:not([multiple]):not([size]):open::picker-icon {
    transform: translateY(2px) rotate(225deg);
  }

  select:not([multiple]):not([size]):open {
    border-color: var(--secondary);
    box-shadow: 0 0 0 3px var(--secondary-surface);
  }

  select:not([multiple]):not([size])::picker(select) {
    margin-block: 6px;
    padding: 5px;
    max-block-size: min(320px, 45dvh);
    inline-size: max-content;
    min-inline-size: min(anchor-size(self-inline), 36rem);
    max-inline-size: min(36rem, calc(100vw - 24px));
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: var(--border-strong) transparent;
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    background: var(--surface);
    color: var(--text);
    box-shadow: var(--shadow-lg);
    font: 400 13px/1.5 var(--font-family-sans);
  }

  select:not([multiple]):not([size]) option {
    display: flex;
    align-items: center;
    gap: 12px;
    min-block-size: 38px;
    padding: 9px 12px;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--text);
    white-space: normal;
    overflow-wrap: anywhere;
    cursor: pointer;
  }

  select:not([multiple]):not([size]) option + option { margin-block-start: 2px; }
  select:not([multiple]):not([size]) option:checked {
    background: var(--secondary-surface);
    color: var(--primary);
    font-weight: 600;
  }
  select:not([multiple]):not([size]) option:not(:disabled):hover,
  select:not([multiple]):not([size]) option:not(:disabled):focus-visible {
    background: var(--primary-surface);
    outline: 1px solid var(--primary-border);
    outline-offset: -1px;
  }
  select:not([multiple]):not([size]) option::checkmark {
    order: 1;
    margin-inline-start: auto;
    color: var(--secondary);
    font-weight: 700;
  }
  select:not([multiple]):not([size]) option:disabled {
    color: var(--text-subtle);
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    select:not([multiple]):not([size]),
    select:not([multiple]):not([size])::picker-icon { transition: none; }
  }
}

.form-label {
  display: inline-block;
  margin-bottom: var(--space-2);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--dark-gray);
}
.form-hint {
  display: block;
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
  color: var(--medium-gray);
}
.form-error {
  display: block;
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
  color: var(--accent-dark);
}
.form-group { margin-bottom: var(--space-4); }

/* ---------- Card ---------- */
.card {
  background-color: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  overflow: hidden;
}
.card-header {
  padding: var(--space-4) var(--space-6);
  border-bottom: 1px solid var(--border-light);
  background-color: var(--surface-alt);
}
.card-body { padding: var(--space-6); }
.card-footer {
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--border-light);
  background-color: var(--surface-alt);
}

/* ---------- Badges ---------- */
.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 2px 0.5rem;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  border-radius: var(--radius);
  border: 1px solid transparent;
  background-color: var(--light-gray);
  color: var(--dark-gray);
}
.badge-primary { background-color: rgba(30, 58, 95, 0.08);  color: var(--primary);     border-color: rgba(30, 58, 95, 0.2); }
.badge-secondary{background-color: rgba(37, 99, 235, 0.08); color: var(--secondary-dark); border-color: rgba(37, 99, 235, 0.2); }
.badge-success { background-color: rgba(5, 150, 105, 0.08); color: var(--success-dark); border-color: rgba(5, 150, 105, 0.2); }
.badge-warning { background-color: rgba(217, 119, 6, 0.08); color: var(--warning-dark); border-color: rgba(217, 119, 6, 0.2); }
.badge-danger  { background-color: rgba(220, 38, 38, 0.08); color: var(--accent-dark);  border-color: rgba(220, 38, 38, 0.2); }
.badge-info    { background-color: rgba(8, 145, 178, 0.08); color: var(--info-dark);    border-color: rgba(8, 145, 178, 0.2); }

/* ---------- Table ---------- */
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
  background-color: var(--white);
}
.table thead th {
  padding: var(--space-3) var(--space-4);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--medium-gray);
  background-color: var(--surface-alt);
  border-bottom: 1px solid var(--border);
  text-align: left;
  white-space: nowrap;
}
.table tbody td {
  padding: var(--space-4);
  border-bottom: 1px solid var(--border-light);
  vertical-align: top;
  color: var(--dark-gray);
}
.table tbody tr { transition: background-color var(--transition-fast); }
.table tbody tr:hover { background-color: var(--surface-alt); }
.table tbody tr:last-child td { border-bottom: none; }

/* ---------- Alert ---------- */
.alert {
  padding: var(--space-3) var(--space-4);
  margin-bottom: var(--space-4);
  border: 1px solid transparent;
  border-left-width: 4px;
  border-radius: var(--radius);
  font-size: var(--font-size-sm);
}
.alert-success { color: var(--success-dark); background-color: #ecfdf5; border-color: #a7f3d0; border-left-color: var(--success); }
.alert-error,
.alert-danger  { color: var(--accent-dark);  background-color: #fef2f2; border-color: #fecaca; border-left-color: var(--accent); }
.alert-warning { color: var(--warning-dark); background-color: #fffbeb; border-color: #fde68a; border-left-color: var(--warning); }
.alert-info    { color: var(--info-dark);    background-color: #ecfeff; border-color: #a5f3fc; border-left-color: var(--info); }

/* ---------- Navigation Tabs ---------- */
.nav-tabs {
  display: flex;
  gap: var(--space-1);
  border-bottom: 1px solid var(--border);
  margin-bottom: var(--space-6);
  overflow-x: auto;
}
.nav-tab {
  padding: var(--space-3) var(--space-4);
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--medium-gray);
  transition: all var(--transition-fast);
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}
.nav-tab:hover { color: var(--dark); }
.nav-tab.active {
  color: var(--secondary-dark);
  border-bottom-color: var(--secondary);
}

/* ---------- Empty State ---------- */
.empty-state {
  text-align: center;
  padding: var(--space-16) var(--space-6);
  color: var(--medium-gray);
}
.empty-state__icon {
  width: 56px;
  height: 56px;
  margin: 0 auto var(--space-4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  background-color: var(--light-gray);
  color: var(--medium-gray);
}
.empty-state__title {
  font-family: var(--font-family-serif);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--dark);
  margin: 0 0 var(--space-2);
}
.empty-state__desc {
  font-size: var(--font-size-sm);
  color: var(--medium-gray);
  max-width: 420px;
  margin: 0 auto var(--space-6);
}

/* ---------- Page Header (used by PageHeader.vue) ---------- */
.page-shell {
  max-width: var(--content-max);
  margin: 0 auto;
  padding: var(--space-8) var(--space-6);
}
.page-shell--wide { max-width: var(--content-max-wide); }

/* ---------- Utility classes ---------- */
.text-center { text-align: center; }
.text-left   { text-align: left; }
.text-right  { text-align: right; }

.text-muted     { color: var(--medium-gray); }
.text-primary   { color: var(--primary); }
.text-secondary { color: var(--secondary); }
.text-success   { color: var(--success); }
.text-warning   { color: var(--warning); }
.text-danger    { color: var(--accent); }

.bg-light   { background-color: var(--light); }
.bg-white   { background-color: var(--white); }
.bg-surface { background-color: var(--surface-alt); }

.border        { border: 1px solid var(--border); }
.border-0      { border: 0; }
.border-top    { border-top: 1px solid var(--border); }
.border-bottom { border-bottom: 1px solid var(--border); }

.rounded     { border-radius: var(--radius); }
.rounded-lg  { border-radius: var(--radius-lg); }
.rounded-xl  { border-radius: var(--radius-xl); }
.rounded-full{ border-radius: var(--radius-full); }

.shadow-sm { box-shadow: var(--shadow-sm); }
.shadow    { box-shadow: var(--shadow); }
.shadow-md { box-shadow: var(--shadow-md); }

.d-flex         { display: flex; }
.d-inline-flex  { display: inline-flex; }
.d-block        { display: block; }
.d-inline-block { display: inline-block; }
.d-none         { display: none; }

.flex-column { flex-direction: column; }
.flex-wrap   { flex-wrap: wrap; }
.flex-1      { flex: 1 1 auto; }

.justify-content-between { justify-content: space-between; }
.justify-content-center  { justify-content: center; }
.justify-content-end     { justify-content: flex-end; }
.align-items-center      { align-items: center; }
.align-items-start       { align-items: flex-start; }

.gap-1 { gap: var(--space-1); }
.gap-2 { gap: var(--space-2); }
.gap-3 { gap: var(--space-3); }
.gap-4 { gap: var(--space-4); }
.gap-6 { gap: var(--space-6); }

.m-0  { margin: 0; }
.mt-0 { margin-top: 0; }  .mt-1 { margin-top: var(--space-1); }  .mt-2 { margin-top: var(--space-2); }
.mt-3 { margin-top: var(--space-3); }  .mt-4 { margin-top: var(--space-4); }  .mt-6 { margin-top: var(--space-6); }
.mb-0 { margin-bottom: 0; } .mb-1 { margin-bottom: var(--space-1); } .mb-2 { margin-bottom: var(--space-2); }
.mb-3 { margin-bottom: var(--space-3); } .mb-4 { margin-bottom: var(--space-4); } .mb-6 { margin-bottom: var(--space-6); }

.p-0 { padding: 0; }
.p-2 { padding: var(--space-2); }
.p-3 { padding: var(--space-3); }
.p-4 { padding: var(--space-4); }
.p-6 { padding: var(--space-6); }

/* Container */
.container {
  width: 100%;
  max-width: var(--content-max);
  margin: 0 auto;
  padding: 0 var(--space-6);
}
.container-sm { max-width: 800px; }
.container-lg { max-width: var(--content-max-wide); }

/* ---------- Scrollbar polish (webkit) ---------- */
::-webkit-scrollbar { width: 10px; height: 10px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: var(--radius-full); border: 2px solid var(--light); }
::-webkit-scrollbar-thumb:hover { background: var(--medium-gray); }

/* ---------- Responsive ---------- */
@media (max-width: 768px) {
  .container, .page-shell { padding-left: var(--space-4); padding-right: var(--space-4); }
  .page-shell { padding-top: var(--space-6); padding-bottom: var(--space-6); }
  h1 { font-size: var(--font-size-2xl); }
  h2 { font-size: var(--font-size-xl); }
}

/* ---------- Accessibility ---------- */
*:focus { outline: none; }
*:focus-visible {
  outline: 2px solid var(--secondary);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* ---------- Print ---------- */
@media print {
  .btn, .nav-tabs, .card-header, .card-footer, .app-shell__nav { display: none !important; }
  .card { border: none; box-shadow: none; }
  body { background: var(--white); }
}
</style>
